import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { authApi } from '../utils/api';

// PRD-007 F13 — account-level notification preferences.
// v1 surface: a single "Pause all email notifications" toggle.
// The "we don't send marketing email, ever" line is load-bearing
// trust copy per UIUX Spec.
export default function NotificationSettings() {
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await authApi.getNotificationPreferences();
        setPaused(r.data.preferences?.paused === true);
      } catch (err) {
        setError(err.response?.data?.error || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onToggle = async () => {
    setSaving(true);
    setError(null);
    try {
      const next = !paused;
      const r = await authApi.setNotificationPreferences({ paused: next });
      setPaused(r.data.preferences?.paused === true);
      setSavedAt(new Date());
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-gray-900">Email notifications</h1>
          <p className="text-sm text-gray-600 mt-1">
            Receive email when something happens that you should know about — Connection invitations, Proposals shared with you, Deliverables submitted for your review, Comments on Proposals you participate in. We don't send marketing email, ever.
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-md shadow-sm p-5">
          {loading ? (
            <div className="text-sm text-gray-600">Loading…</div>
          ) : (
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-medium text-gray-900">Pause all email notifications</div>
                <div className="text-sm text-gray-600 mt-1">
                  {paused
                    ? "You'll still see notifications inside Verdict when you log in. We'll resume sending email when you toggle this off."
                    : 'Email notifications are on for your account.'}
                </div>
              </div>
              <button
                onClick={onToggle}
                disabled={saving}
                role="switch"
                aria-checked={paused}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                  paused ? 'bg-primary-600' : 'bg-gray-300'
                } ${saving ? 'opacity-60' : ''}`}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${
                    paused ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          )}
          {error && (
            <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>
          )}
          {savedAt && !error && (
            <div className="mt-3 text-xs text-gray-600">Saved at {savedAt.toLocaleTimeString()}.</div>
          )}
        </div>

        <p className="text-xs text-gray-600 mt-4">
          Tip: you can also pause notifications by following the unsubscribe link at the bottom of any Verdict email.
        </p>
      </div>
    </Layout>
  );
}
